'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {
  FaApple, FaBarcode, FaCamera, FaChartLine, FaShieldAlt, FaFlask, FaCheck, FaStar,
  FaLeaf, FaMapMarkerAlt, FaFire, FaTrophy, FaMedal, FaBolt, FaSeedling, FaStore, FaUtensils,
  FaArrowRight,
} from 'react-icons/fa'
import Breadcrumbs from '@/app/components/Breadcrumbs'
import AppStoreLink from '@/app/components/AppStoreLink'

// Every feature below is verified against the shipped iOS app source
// (scan + 0–100 score, safer alternatives, exposure tracking, the
// Passport/MicroQuests/Weekly-Challenges loop, the Local Eco-Friendly Guide, chemical
// flags) and the app's own Credits screen (PlasticList Bay Area data +
// Blueprint inspiration). Nothing here is invented.

const AT_A_GLANCE = [
  { icon: FaBarcode, title: 'Scan any product', body: 'Barcode + a packaging photo returns a 0–100 microplastic risk score in seconds.' },
  { icon: FaLeaf, title: 'Get a safer swap', body: 'When a score is high, the app suggests a cleaner same-category alternative.' },
  { icon: FaChartLine, title: 'Track your exposure', body: 'Daily, weekly, and monthly exposure trends from everything you scan.' },
  { icon: FaTrophy, title: 'Build the habit', body: 'Plastic Passport stamps, MicroQuests, and weekly challenges keep you scanning past day one.' },
  { icon: FaMapMarkerAlt, title: 'Local Eco Guide', body: 'Find zero-waste groceries, sustainable dining, and farmers markets near you.' },
  { icon: FaFlask, title: 'Backed by real data', body: 'Scores tie to peer-reviewed research and PlasticList’s Bay Area food testing.' },
  { icon: FaBolt, title: 'Ask the Exposure Coach', body: 'An AI coach that answers questions about any scan, with the sources it drew from.' },
  { icon: FaStore, title: 'Shopping Mode', body: 'Scan your whole cart in-store and get one exposure report before checkout.' },
  { icon: FaFire, title: 'Audit every room', body: 'Guided room-by-room audits give each room a score and its best next action.' },
]

const ECO_CATEGORIES = [
  { icon: FaSeedling, title: 'Zero-Waste Grocery', body: 'Bulk stores and eco-friendly shopping near you.' },
  { icon: FaUtensils, title: 'Sustainable Dining', body: 'Farm-to-table and organic restaurants.' },
  { icon: FaStore, title: 'Local Markets', body: 'Farmers markets and fresh produce.' },
]

const SCAN_CATEGORIES = [
  'Bottled water & drinks', 'Baby foods', 'Fresh foods', 'Snacks',
  'Frozen meals', 'Canned foods', 'Takeout containers', 'Meal shakes',
  'Protein bars', 'Supplements', 'Cosmetics & skincare', 'Cookware & storage',
]

export default function FeaturesContent() {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumbs */}
      <div className="frame pt-6">
        <div className="w-full">
          <Breadcrumbs items={[{ label: 'Features' }]} />
        </div>
      </div>

      {/* HERO */}
      <section className="frame pt-8 pb-12 md:pt-10 md:pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <div>
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
              Inside the app
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink mb-5">
              Everything the <span className="text-forest">MicroPlastics app</span> does
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6">
              Scan a product for a research-backed risk score, get a cleaner
              swap, watch your exposure drop over time, and find low-plastic
              places near you. One app for the whole loop, not just a lookup.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <AppStoreLink
                source="features_hero"
                className="inline-flex items-center justify-center gap-2.5 bg-ink text-cream px-7 py-3.5 rounded-control hover:bg-ink/90 transition-colors font-semibold min-h-[48px] pressable"
              >
                <FaApple className="w-6 h-6" aria-hidden="true" />
                <span>Download on iOS</span>
              </AppStoreLink>
              <Link
                href="/microplastics-scanner-app"
                className="inline-flex items-center justify-center gap-2 bg-white border border-black/10 text-gray-800 px-7 py-4 rounded-xl hover:border-forest transition font-semibold"
              >
                How the scanner works
              </Link>
            </div>
            <p className="text-sm text-gray-500">
              Free to start with 3 scans · 3-day free trial on Premium · iOS · No account required for your first scan
            </p>
          </div>
          <div className="flex justify-center md:justify-end">
            <AppStoreLink
              source="features_hero_screenshot"
              aria-label="Download MicroPlastics on the App Store"
              className="block group"
            >
              <div className="relative w-[240px] sm:w-[280px] rounded-[2.6rem] border-[6px] border-ink/90 bg-ink shadow-[0_24px_60px_rgba(21,35,25,0.28)] overflow-hidden transition-transform duration-300 group-hoverable:scale-[1.015]">
                <Image
                  src="/screens/today.webp"
                  alt="MicroPlastics app Today screen: exposure gauge, rooms, and daily quest"
                  width={880}
                  height={1912}
                  priority
                  sizes="280px"
                  className="w-full h-auto rounded-[2.2rem]"
                />
              </div>
            </AppStoreLink>
          </div>
        </div>
      </section>

      {/* AT A GLANCE GRID */}
      <section className="frame pb-14 md:pb-16">
        <div className="w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
            {AT_A_GLANCE.map((f) => (
              <div key={f.title} className="border-t border-ink/10 pt-5">
                <div className="mb-3">
                  <f.icon className="w-5 h-5 text-forest" aria-hidden="true" />
                </div>
                <h2 className="font-bold text-lg text-ink mb-1.5">{f.title}</h2>
                <p className="text-gray-600 leading-relaxed text-sm">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1. SCAN + SCORE */}
      <section className="bg-cream py-14 md:py-16">
        <div className="frame">
          <div className="flex items-center gap-2 mb-3">
            <FaCamera className="w-5 h-5 text-forest" aria-hidden="true" />
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest">Scan &amp; score</p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
            A 0–100 risk score for anything with a barcode
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            Tap the barcode to pull the brand, category, and declared packaging.
            Then snap a photo of the actual container. The scoring model reads
            the material and physical condition (scratches, dents, fade, cap
            style) that a barcode alone can&apos;t see. You get a 0–100 microplastic
            risk score with the <strong>cited published research behind it</strong>,
            so it&apos;s never an opaque number.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-2">
            {[
              { icon: FaBarcode, step: '1. Scan the barcode', body: 'Brand, category, and packaging spec load instantly.' },
              { icon: FaCamera, step: '2. Snap the packaging', body: 'Condition scoring sees what barcode-only apps miss.' },
              { icon: FaChartLine, step: '3. Get the score + research', body: '0–100 risk with the studies that informed it.' },
            ].map((s) => (
              <div key={s.step} className="border-t border-ink/10 pt-5">
                <s.icon className="w-6 h-6 text-forest mb-3" aria-hidden="true" />
                <h3 className="font-bold text-ink mb-1">{s.step}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. SAFER ALTERNATIVES */}
      <section className="frame py-14 md:py-16">
        <div className="flex items-center gap-2 mb-3">
          <FaLeaf className="w-5 h-5 text-forest" aria-hidden="true" />
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest">Safer swaps</p>
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
          A cleaner alternative when the score is high
        </h2>
        <p className="text-gray-600 leading-relaxed max-w-3xl">
          A score you can&apos;t act on isn&apos;t worth much. When a product scores
          high, the app surfaces a <strong>safer same-category swap</strong>: the
          glass-jarred option instead of the plastic tub, the lower-plastic
          bottle instead of the single-use PET. You leave the shelf with a
          decision, not just a warning.
        </p>
      </section>

      {/* 3. TRACK + GAMIFICATION */}
      <section className="bg-cream py-14 md:py-16">
        <div className="frame">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="order-2 md:order-1 flex justify-center">
              <AppStoreLink
                source="features_tracking_screenshot"
                aria-label="Download MicroPlastics on the App Store"
                className="block group"
              >
                <div className="relative w-[240px] sm:w-[270px] rounded-[2.6rem] border-[6px] border-ink/90 bg-ink shadow-[0_24px_60px_rgba(21,35,25,0.28)] overflow-hidden transition-transform duration-300 group-hoverable:scale-[1.015]">
                  <Image
                    src="/screens/reports.webp"
                    alt="MicroPlastics app reports screen: exposure trends and period comparisons"
                    width={880}
                    height={1912}
                    sizes="270px"
                    className="w-full h-auto rounded-[2.2rem]"
                  />
                </div>
              </AppStoreLink>
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center gap-2 mb-3">
                <FaChartLine className="w-5 h-5 text-forest" aria-hidden="true" />
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest">Track &amp; stay motivated</p>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
                Watch your exposure trend down
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every scan feeds your personal tracking dashboard: daily,
                weekly, and monthly exposure trends and stats, plus a saved log
                of what you scanned. A one-time score tells you about a product;
                the trend line tells you whether your swaps are actually working.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: FaChartLine, t: 'Exposure trends', d: 'Daily, weekly, and monthly views with saved scan history.' },
                  { icon: FaMedal, t: 'Plastic Passport', d: 'Collect a stamp for every distinct plastic source you uncover.' },
                  { icon: FaBolt, t: 'MicroQuests', d: '10-minute guided scanning missions with a badge at the end.' },
                  { icon: FaFire, t: 'Weekly Challenges', d: 'One fresh themed challenge every Monday, the same week for everyone.' },
                ].map((r) => (
                  <li key={r.t} className="flex gap-3">
                    <span className="shrink-0 w-9 h-9 rounded-lg bg-white flex items-center justify-center border border-black/5">
                      <r.icon className="w-4 h-4 text-forest" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block font-semibold text-ink text-sm">{r.t}</span>
                      <span className="block text-sm text-gray-600 leading-relaxed">{r.d}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3.4 BEYOND THE SCANNER — the app features the site never
          mentioned before the July 2026 feature-parity audit. Every row is
          verified against the app source (coach.tsx, cart.tsx, rooms/,
          plan/, testedLists.ts, watchlist, challenges/, contribute/). */}
      <section className="bg-cream py-14 md:py-16">
        <div className="frame">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
            Beyond the scanner
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
            The rest of the toolkit
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-2xl mb-8">
            Scanning is the front door. Everything after it is built to turn
            one answer into a lower-exposure routine.
          </p>
          <ul className="border-b border-ink/10">
            {[
              { title: 'Exposure Coach', body: 'A scan-aware AI assistant. Ask why a product scored the way it did, or what to buy instead, and get answers with the sources cited.' },
              { title: 'Shopping Mode', body: 'Scan items into a cart while you shop and get a cart exposure report: aggregate score, worst drivers, and the single best swap before you check out.' },
              { title: 'Room audits', body: 'Walk through the bathroom, nursery, or kitchen with a guided audit. Each room gets its own exposure score and a best next action.' },
              { title: '7-day lower-exposure plan', body: 'A one-week plan matched to your goals, one check-off action per day, built from your onboarding answers.' },
              { title: 'Tested product library', body: 'Browse roughly 236 lab-tested products from the PlasticList dataset by category: bottled water, baby and kids, dairy, coffee, and more.' },
              { title: 'Five scanner modes', body: 'Auto, Barcode, Label, Ingredients, and Material modes adapt the camera to what you point it at. No camera? Type the product name instead.' },
              { title: 'Watchlist', body: 'Pick the materials you care about and every future scan flags them automatically.' },
              { title: 'Friend challenges', body: 'Start a challenge, share the code, and compare progress with friends.' },
              { title: 'Contribute and earn', body: 'Hit a barcode the catalog does not know? Submit two photos to add it and earn an extra free scan.' },
            ].map((f) => (
              <li
                key={f.title}
                className="grid grid-cols-1 sm:grid-cols-[16rem_1fr] gap-x-6 gap-y-1 border-t border-ink/10 py-4 sm:py-5"
              >
                <h3 className="font-bold text-ink leading-snug">{f.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{f.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 3.5 HABIT LOOP — product pages for the July 2026 gamification
          system (Passport / MicroQuests / Weekly Challenges / Referral). */}
      <section className="frame py-14 md:py-16">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-gold mb-3">The habit loop</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
            Five features that keep the second scan coming
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Each one has its own page showing how it works.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
          {[
            { href: '/features/cart-scan', title: 'Cart Scan', body: 'Score a whole basket while you shop, with the one swap that helps most.' },
            { href: '/features/plastic-coach', title: 'Plastic Coach', body: 'Ask anything about your scans and swaps, answered from the research.' },
            { href: '/features/plastic-passport', title: 'Plastic Passport', body: 'A stamp for every real discovery. Fill pages, share cards.' },
            { href: '/features/microquests', title: 'MicroQuests', body: 'Guided 10-minute scanning missions with badges.' },
            { href: '/features/weekly-challenges', title: 'Weekly Challenges', body: 'A fresh themed challenge every Monday, same for everyone.' },
            { href: '/features/invite-a-friend', title: 'Invite a Friend', body: 'You both get 1 month of Pro free after their first 3 scans.' },
          ].map((c) => (
            <Link
              key={c.href}
              href={c.href}
              className="group border-t border-ink/10 pt-5"
            >
              <h3 className="font-bold text-ink mb-1.5 inline-flex items-center gap-1.5">
                {c.title}
                <FaArrowRight className="w-3 h-3 text-forest opacity-0 group-hover:opacity-100 transition-opacity duration-200" aria-hidden="true" />
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">{c.body}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 4. LOCAL ECO GUIDE */}
      <section className="frame py-14 md:py-16">
        <div className="flex items-center gap-2 mb-3">
          <FaMapMarkerAlt className="w-5 h-5 text-forest" aria-hidden="true" />
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest">Local Eco-Friendly Guide</p>
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
          Find low-plastic spots near you
        </h2>
        <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
          Reducing exposure is easier when you know where to shop and eat. The
          Local Eco-Friendly Guide uses your location to surface sustainable
          places nearby, with actionable eco tips and a way to suggest spots
          the community should know about.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-2">
          {ECO_CATEGORIES.map((c) => (
            <div key={c.title} className="border-t border-ink/10 pt-5">
              <div className="mb-3">
                <c.icon className="w-5 h-5 text-forest" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-gray-800 mb-1.5">{c.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. CHEMICAL FLAGS */}
      <section className="bg-cream py-14 md:py-16">
        <div className="content text-center">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <FaShieldAlt className="w-5 h-5 text-forest" aria-hidden="true" />
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest">Chemicals of concern</p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
            Beyond microplastics: BPA, BPS, PFAS &amp; phthalates
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto mb-6">
            Microplastics travel with a cast of plastic-related chemicals. The
            app flags the plasticisers and chemicals of concern tied to a
            product so you see the full picture, not just the particles.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['BPA', 'BPS', 'PFAS', 'Phthalates'].map((c) => (
              <span key={c} className="inline-flex items-center gap-2 bg-white border border-black/5 rounded-full px-4 py-2 text-sm font-semibold text-gray-700">
                <FaCheck className="w-3.5 h-3.5 text-forest" aria-hidden="true" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 6. DATA PROVENANCE */}
      <section className="content py-14 md:py-16">
        <div className="flex items-center gap-2 mb-3 justify-center">
          <FaFlask className="w-6 h-6 text-forest" aria-hidden="true" />
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink text-center">
            Built on real testing data
          </h2>
        </div>
        <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
          Every score is grounded in sources you can check, with no wellness-influencer math.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border-t border-ink/10 pt-5">
            <h3 className="font-bold text-gray-800 mb-2">PlasticList: Bay Area food testing</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              The app draws on data from <strong>PlasticList</strong>, the open
              project that lab-tested hundreds of San Francisco Bay Area foods
              for plastic chemicals. We&apos;re grateful they made the data public.
            </p>
            <a
              href="https://plasticlist.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-forest hover:text-deepForest underline"
            >
              plasticlist.org <FaArrowRight className="w-3 h-3" aria-hidden="true" />
            </a>
          </div>
          <div className="border-t border-ink/10 pt-5">
            <h3 className="font-bold text-gray-800 mb-2">Peer-reviewed research</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              Scores also tie to published studies, the same research we
              summarise in our{' '}
              <Link href="/microplastics-research" className="text-forest underline hover:text-deepForest">Research Hub</Link>{' '}
              so every number links back to a primary source.
            </p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Inspired in part by Bryan Johnson&apos;s Blueprint Microplastics Test.
              The app produces a research-based relative risk score, not a lab
              measurement of your specific item.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOU CAN SCAN */}
      <section className="bg-cream py-14 md:py-16">
        <div className="frame">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 text-center">
            What you can scan
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            The scoring model adapts to the category. If it has a barcode, you can check it.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {SCAN_CATEGORIES.map((c) => (
              <span key={c} className="bg-white border border-black/5 rounded-full px-4 py-2 text-sm font-medium text-gray-700">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="frame py-14 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-1 text-gold mb-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <FaStar key={i} className="w-5 h-5" aria-hidden="true" />
            ))}
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-3">
            Try it on your own pantry this week
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6 max-w-2xl mx-auto">
            Three free scans. Scan your water, coffee, food storage, and
            cosmetics. See the score, the research, the swap, and start your
            exposure trend line.
          </p>
          <AppStoreLink
            source="features_final_cta"
            className="inline-flex items-center justify-center gap-2.5 bg-ink text-cream px-8 py-4 rounded-control hover:bg-ink/90 transition-colors shadow-lg shadow-ink/15 font-semibold text-lg min-h-[48px] pressable"
          >
            <FaApple className="w-6 h-6" aria-hidden="true" />
            Download on iOS
          </AppStoreLink>
          <p className="text-sm text-gray-500 mt-4">
            Free · 3 scans included · No credit card required
          </p>
        </div>
      </section>
    </div>
  )
}
